<script lang="ts">
	import { flip } from 'svelte/animate';
	import { CardGrid } from '@Shelly/shelly-design-system';
	import CameraTile from './CameraTile.svelte';
	import EmptyList from '../../Lists/EmptyList.svelte';
	import type { Device } from 'src/types/Device';
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import EmptyPlaceholder from 'src/components/EmptyPlaceholder.svelte';

	export let cameras: Device[] = [];
	export let cols: number = 2;
	export let editMode: boolean = false;
	export let onOpenSettings: () => void = () => {};

	const dispatch = createEventDispatcher();
	const flipDurationMs = 0;
	// DS pattern: drag is disabled until card's drag handle is grabbed
	let dragDisabled = true;
	// Tracks an active drag so the external `cameras` sync below doesn't clobber the
	// DnD working list mid-drag (which would duplicate the dragged item -> duplicate keys).
	let isDragging = false;

	type ReorderableCamera = Device & { isDndShadowItem?: boolean; [key: string]: unknown };
	let reorderableCameras: ReorderableCamera[] = [];

	// Sync from `cameras`, but not mid-drag (would clobber the DnD list and duplicate items).
	$: syncFromCameras(cameras);

	function syncFromCameras(list: Device[]) {
		if (isDragging) return;
		reorderableCameras = list.map((c) => ({ ...c, isDndShadowItem: false })) as ReorderableCamera[];
	}

	// Calculate percentage width to force exact column count
	function getColWidthPercent(colCount: number): number {
		switch (colCount) {
			case 1:
				return 100;
			case 2:
				return 49;
			case 3:
				return 32;
			case 4:
				return 24;
			default:
				return 49;
		}
	}

	$: colWidth = getColWidthPercent(cols);

	function handleSort(e: CustomEvent) {
		reorderableCameras = e.detail.items;
		handleEndDrag();
	}

	function handleFinalizeSort(e: CustomEvent) {
		reorderableCameras = e.detail.items;
		handleEndDrag();
		isDragging = false;
		dispatch('reorder', { items: reorderableCameras });
	}

	const handleStartDrag = () => {
		dragDisabled = false;
		isDragging = true;
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};
</script>

{#if cameras.length === 0}
	<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column align-items-center">
		<EmptyPlaceholder class="mt-md-l mt-sm-s mb-m" />
		<EmptyList
			title={$format('empty_camera_dashboard_label', {
				default: 'Dashboard is empty',
			})}
			description={$format('empty_camera_dashboard_descr', {
				default: 'Customize your dashboard to select which cameras to display.',
			})}
			btnText={$format('button_customize', { default: 'Customize' })}
			onClick={onOpenSettings}
		/>
	</div>
{:else}
	<CardGrid
		class="camera-grid"
		xl={colWidth}
		lg={colWidth}
		md={colWidth}
		sm={colWidth}
		widthUnit="%"
		gridGap="s"
		dndSettings={{
			items: reorderableCameras,
			flipDurationMs,
			dragDisabled,
		}}
		{editMode}
		on:consider={handleSort}
		on:finalize={handleFinalizeSort}
	>
		{#each reorderableCameras as camera (camera.id)}
			<div
				class="camera-grid-item"
				class:dnd-placeholder={camera.isDndShadowItem}
				animate:flip={{ duration: flipDurationMs }}
			>
				{#if !camera.isDndShadowItem}
					<CameraTile
						{camera}
						{editMode}
						on:handle-drag-start={handleStartDrag}
						on:handle-drag-end={handleEndDrag}
					/>
				{/if}
			</div>
		{/each}
	</CardGrid>
{/if}

<style>
	.camera-grid-item {
		height: 100%;
	}

	/* DnD placeholder styling */
	.dnd-placeholder {
		border: 2px dashed var(--color-border);
		border-radius: var(--radius-extra-big);
		background: transparent;
		aspect-ratio: 16 / 9;
	}
</style>
